import { inject, Injectable } from '@angular/core';
import { EcaSecurityService } from '@drc-eca/eca-components-lib';
import { SecurityServiceUserResponse } from '@drc-eca/eca-components-lib';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { Observable, from, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { PortalError } from '../../shared/portal-error.model';
import { UserManagementService } from './user-management.service';
import { OktaService } from '../../core/services/okta.service';

@Injectable({
  providedIn: 'root'
})
export class UserSecurityService extends EcaSecurityService {
  static badPasswordMessage = 'There was a problem updating your password. Do not reuse any of your last five passwords. Do not use your first or last name or your username.';
  static expiredPasswordMessage = 'Your link to reset your password has expired. To receive a new password reset link, click on Send a New Link.';
  private userManagementService = inject(UserManagementService);
  private oktaService = inject(OktaService);
  private oktaPasswordComplexityRequirementsPromise?: Promise<any>;

  constructor() {
    super();
  }

  updateUserEca(userProfileObj: any): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/user/${userProfileObj.userid}`;
    const reqOptions = { includeToken: true, cache: { cache: false, clearKey: this.getUserCacheKey(userProfileObj.userid) } };
    return this.apiGatewayService.put<SecurityServiceUserResponse>(url, userProfileObj, reqOptions).pipe(
      tap(user => this.sendPermissionChangeAlert(user.userId).subscribe()),
      switchMap(() => this.resetToken()));
  }

  updateUser(userProfileObj: any): Observable<SecurityServiceUserResponse> {
    if (userProfileObj.password && userProfileObj.currentPassword) {
      return this.userManagementService.changePassword(userProfileObj.userid, userProfileObj.currentPassword, userProfileObj.password).pipe(
        switchMap(() => this.resetToken()),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(user => this.sendPermissionChangeAlert(userProfileObj.userid).subscribe()),
        catchError((error) => {
          // If 401 (from token authenticator - user is not in UUM) then try ECA
          if (error.status === 401) {
            return this.updateUserEca(userProfileObj);
          }
          return throwError(error);
        })
      );
    }

    return this.updateUserEca(userProfileObj);
  }

  updateUserWithOktaFallback(userProfileObj: any): Observable<any> {
    if (!userProfileObj.password || !userProfileObj.currentPassword) {
      return this.updateUser(userProfileObj);
    }

    return from(this.oktaService.changePassword(userProfileObj.currentPassword, userProfileObj.password)).pipe(
      switchMap(() => this.resetToken()),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tap(user => this.sendPermissionChangeAlert(userProfileObj.userid).subscribe()),
      catchError((error) => {
        if (this.shouldFallbackFromOktaToEca(error)) {
          return this.updateUser(userProfileObj);
        }
        return throwError(error);
      })
    );
  }

  validatePasswordWithOktaFallback(userId: string, newPassword: string, token?: string, userData?: any): Observable<any> {
    return from(this.getOktaPasswordComplexityRequirements()).pipe(
      map((policy) => {
        console.debug('Okta policy retrieved, building validation rules');
        return this.buildValidationFromOktaPolicy(newPassword, policy, userData);
      }),
      catchError((err) => {
        const errorMsg = err?.message || JSON.stringify(err);
        const isCorsError = errorMsg.includes('CORS') || errorMsg.includes('Failed to fetch');
        console.warn(`Okta policy fetch failed (${isCorsError ? 'CORS' : 'other error'}), falling back to ECA/UUM:`, err);
        return this.userManagementService.validatePassword(userId, newPassword, token).pipe(
          map((validation) => this.ensureDefaultNameChecks(validation, newPassword, userData))
        );
      })
    );
  }

  private getOktaPasswordComplexityRequirements(): Promise<any> {
    if (!this.oktaPasswordComplexityRequirementsPromise) {
      this.oktaPasswordComplexityRequirementsPromise = this.oktaService.getPasswordComplexityRequirements();
    }

    return this.oktaPasswordComplexityRequirementsPromise;
  }

  private shouldFallbackFromOktaToEca(error: any): boolean {
    const errorCode = error?.errorCode || error?.error?.errorCode;
    const status = error?.status;

    // Non-Okta and non-Okta-session cases should use existing ECA/UUM flow.
    return status === 401 ||
      status === 404 ||
      errorCode === 'E0000005' ||
      errorCode === 'E0000007' ||
      errorCode === 'E0000011';
  }

  private buildValidationFromOktaPolicy(newPassword: string, policyResponse: any, userData?: any): any {
    const complexity = policyResponse?.complexity || policyResponse || {};

    const minLength = this.getPolicyNumber(complexity, ['minLength', 'min_length']);
    const minLowerCase = this.getPolicyNumber(complexity, ['minLowerCase', 'min_lower_case']);
    const minUpperCase = this.getPolicyNumber(complexity, ['minUpperCase', 'min_upper_case']);
    const minNumber = this.getPolicyNumber(complexity, ['minNumber', 'min_number']);
    const minSymbol = this.getPolicyNumber(complexity, ['minSymbol', 'min_symbol']);
    const excludeAttributes = this.normalizeExcludeAttributes(policyResponse?.excludeAttributes || policyResponse?.exclude_attributes || []);
    const excludeUsername = policyResponse?.excludeUsername || false;

    const lowerCount = (newPassword.match(/[a-z]/g) || []).length;
    const upperCount = (newPassword.match(/[A-Z]/g) || []).length;
    const numberCount = (newPassword.match(/[0-9]/g) || []).length;
    const symbolCount = (newPassword.match(/[^A-Za-z0-9]/g) || []).length;

    const checks: any[] = [];

    if (minLength > 0) {
      checks.push({
        name: 'length',
        valid: newPassword.length >= minLength,
        rule: `Minimum length is ${minLength} characters`
      });
    }

    if (minUpperCase > 0) {
      checks.push({
        name: 'uppercase',
        valid: upperCount >= minUpperCase,
        rule: `Must have at least ${minUpperCase} uppercase letter(s)`
      });
    }

    if (minLowerCase > 0) {
      checks.push({
        name: 'lowercase',
        valid: lowerCount >= minLowerCase,
        rule: `Must have at least ${minLowerCase} lowercase letter(s)`
      });
    }

    if (minNumber > 0) {
      checks.push({
        name: 'number',
        valid: numberCount >= minNumber,
        rule: `Must have at least ${minNumber} numeric character`
      });
    }

    if (minSymbol > 0) {
      checks.push({
        name: 'symbol',
        valid: symbolCount >= minSymbol,
        rule: `Must have at least ${minSymbol} special character(s)`
      });
    }

    // Handle excludeUsername
    if (excludeUsername && userData?.username) {
      const usernameCheckValid = !this.passwordContainsValue(newPassword, userData.username);
      checks.push({
        name: 'excludeUsername',
        valid: usernameCheckValid,
        rule: `Must not contain your own email, or chunks from your own email`
      });
    }

    // Handle excludeAttributes
    let hasNameExcludeAttribute = false;
    for (const attribute of excludeAttributes) {
      if (this.isNameAttribute(attribute)) {
        hasNameExcludeAttribute = true;
        continue;
      }

      const value = this.getUserAttributeValue(userData, attribute);
      const attributeCheckValid = value ? !this.passwordContainsValue(newPassword, value) : true;
      const attributeLabel = this.formatAttributeLabel(attribute);
      checks.push({
        name: `exclude${attribute}`,
        valid: attributeCheckValid,
        rule: `Must not contain your own ${attributeLabel}`
      });
    }

    this.addDefaultNameExclusionChecks(checks, newPassword, userData, hasNameExcludeAttribute);

    // Handle email exclusion (similar to firstName/lastName)
    if (userData?.email) {
      const emailCheckValid = !this.passwordContainsEmailChunks(newPassword, userData.email);
      checks.push({
        name: 'excludeEmail',
        valid: emailCheckValid,
        rule: `Must not contain your own email, or chunks from your own email`
      });
    }

    if (!checks.length) {
      throw new Error('No complexity rules returned by Okta policy response');
    }

    const isValid = checks.every((check) => check.valid);
    return {
      status: isValid ? 'OK' : 'INVALID',
      checks
    };
  }

  private ensureDefaultNameChecks(validation: any, newPassword: string, userData?: any): any {
    if (!validation || typeof validation !== 'object') {
      return validation;
    }

    const checks = Array.isArray(validation.checks) ? [...validation.checks] : [];
    this.addDefaultNameExclusionChecks(checks, newPassword, userData, false);
    const originalStatus = String(validation.status || '').toUpperCase();

    return {
      ...validation,
      status: originalStatus === 'INVALID' || checks.some((check) => check.valid === false) ? 'INVALID' : 'OK',
      checks
    };
  }

  private addDefaultNameExclusionChecks(checks: any[], newPassword: string, userData?: any, hasNameExcludeAttribute?: boolean): void {
    if (!userData) {
      return;
    }

    const firstName = this.getUserAttributeValue(userData, 'firstName');
    const lastName = this.getUserAttributeValue(userData, 'lastName');
    const shouldInclude = hasNameExcludeAttribute || !!firstName || !!lastName;

    if (!shouldInclude) {
      return;
    }

    const combinedValid = this.isCombinedNameCheckValid(newPassword, firstName, lastName);
    this.replaceNameChecksWithCombined(checks, combinedValid);
  }

  private replaceNameChecksWithCombined(checks: any[], combinedValid: boolean): void {
    const existingCombinedCheck = checks.find((check) => this.isCombinedNameCheck(check));
    const hasInvalidExistingNameCheck = checks
      .filter((check) => this.isNameCheck(check))
      .some((check) => check.valid === false);

    for (let i = checks.length - 1; i >= 0; i--) {
      if (this.isNameCheck(checks[i])) {
        checks.splice(i, 1);
      }
    }

    const valid = combinedValid && !hasInvalidExistingNameCheck;
    if (existingCombinedCheck) {
      checks.push({
        ...existingCombinedCheck,
        name: 'excludeName',
        valid,
        rule: 'Must not contain your own name'
      });
      return;
    }

    checks.push({
      name: 'excludeName',
      valid,
      rule: 'Must not contain your own name'
    });
  }

  private isCombinedNameCheckValid(newPassword: string, firstName?: string, lastName?: string): boolean {
    const containsFirstName = firstName ? this.passwordContainsValue(newPassword, firstName) : false;
    const containsLastName = lastName ? this.passwordContainsValue(newPassword, lastName) : false;

    return !containsFirstName && !containsLastName;
  }

  private isNameAttribute(attribute: string): boolean {
    const normalized = this.normalizeAttributeKey(attribute);
    return normalized === 'firstname' || normalized === 'givenname' || normalized === 'lastname' || normalized === 'familyname' || normalized === 'surname';
  }

  private isNameCheck(check: any): boolean {
    const normalizedName = this.normalizeAttributeKey(check?.name || '');
    const normalizedRule = (check?.rule || '').toLowerCase();

    return this.isCombinedNameCheck(check) ||
      normalizedName.includes('excludefirstname') ||
      normalizedName.includes('excludegivenname') ||
      normalizedName.includes('excludelastname') ||
      normalizedName.includes('excludefamilyname') ||
      normalizedName.includes('excludesurname') ||
      normalizedRule.includes('first name') ||
      normalizedRule.includes('last name');
  }

  private isCombinedNameCheck(check: any): boolean {
    const normalizedName = this.normalizeAttributeKey(check?.name || '');
    const normalizedRule = (check?.rule || '').toLowerCase();

    return normalizedName.includes('excludename') || normalizedRule.includes('own name');
  }

  private passwordContainsValue(password: string, value: string): boolean {
    if (!value) return false;
    const lowerPassword = password.toLowerCase();
    const lowerValue = value.toLowerCase();
    return lowerPassword.includes(lowerValue);
  }

  private passwordContainsEmailChunks(password: string, email: string): boolean {
    if (!email) return false;

    const lowerPassword = password.toLowerCase();

    // Split email into parts: local part and domain
    const [localPart, ...domainParts] = email.split('@');
    const domain = domainParts.join('@');

    // Check if password contains the entire email
    if (lowerPassword.includes(email.toLowerCase())) {
      return true;
    }

    // Check if password contains local part (part before @)
    if (localPart && lowerPassword.includes(localPart.toLowerCase())) {
      return true;
    }

    // Check if password contains domain parts
    if (domain) {
      // Split domain by common separators
      const domainChunks = domain.split(/[.\-_]/);
      for (const chunk of domainChunks) {
        if (chunk && lowerPassword.includes(chunk.toLowerCase())) {
          return true;
        }
      }
    }

    return false;
  }

  private formatAttributeLabel(attribute: string): string {
    const normalized = this.normalizeAttributeKey(attribute);
    if (normalized === 'firstname' || normalized === 'givenname') {
      return 'first name';
    }
    if (normalized === 'lastname' || normalized === 'familyname' || normalized === 'surname') {
      return 'last name';
    }

    // Convert camelCase or snake_case to readable format
    return attribute
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .toLowerCase()
      .trim();
  }

  private normalizeExcludeAttributes(rawExcludeAttributes: any[]): string[] {
    if (!Array.isArray(rawExcludeAttributes)) {
      return [];
    }

    const normalized = rawExcludeAttributes
      .map((attribute) => {
        if (typeof attribute === 'string') {
          return attribute;
        }
        if (attribute && typeof attribute.name === 'string') {
          return attribute.name;
        }
        if (attribute && typeof attribute.key === 'string') {
          return attribute.key;
        }
        return null;
      })
      .filter((attribute): attribute is string => !!attribute)
      .map((attribute) => attribute.trim())
      .filter((attribute) => !!attribute);

    return [...new Set(normalized)];
  }

  private getUserAttributeValue(userData: any, attribute: string): string | undefined {
    if (!userData) {
      return undefined;
    }

    const directValue = userData?.[attribute];
    if (typeof directValue === 'string' && directValue) {
      return directValue;
    }

    const normalizedAttribute = this.normalizeAttributeKey(attribute);
    const aliases = this.getAttributeAliases(normalizedAttribute);

    for (const [key, value] of Object.entries(userData)) {
      if (typeof value !== 'string' || !value) {
        continue;
      }

      const normalizedKey = this.normalizeAttributeKey(key);
      if (normalizedKey === normalizedAttribute || aliases.includes(normalizedKey)) {
        return value;
      }
    }

    return undefined;
  }

  private getAttributeAliases(normalizedAttribute: string): string[] {
    if (normalizedAttribute === 'firstname' || normalizedAttribute === 'givenname') {
      return ['firstname', 'givenname'];
    }

    if (normalizedAttribute === 'lastname' || normalizedAttribute === 'familyname' || normalizedAttribute === 'surname') {
      return ['lastname', 'familyname', 'surname'];
    }

    return [normalizedAttribute];
  }

  private normalizeAttributeKey(key: string): string {
    return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  private getPolicyNumber(policy: any, keys: string[]): number {
    for (const key of keys) {
      const value = policy?.[key];
      if (typeof value === 'number' && !isNaN(value)) {
        return value;
      }
    }
    return 0;
  }

  sendPermissionChangeAlert(userId: string): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/email/permissionChange/${userId}`;
    const reqOptions = { includeToken: true, cache: { cache: false } };
    return this.apiGatewayService.post<any>(url, null, reqOptions).pipe(
      map(response => response));
  }

  // need to reset the token, so that the header updates
  // but want to pass user through as a response
  private resetToken() {
    return this.appContextService.getToken().pipe(
      tap(token => this.appContextService.setToken(token))
    );
  }

  activateAccountEca(newCredentialsObj: any): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/password/reset`;
    const reqOptions = { includeToken: false, cache: { cache: false } };
    return this.apiGatewayService.put<any>(url, newCredentialsObj, reqOptions).pipe(
      map(response => response),
      catchError(err => this.handleHttpError(err, UserSecurityService.badPasswordMessage)));
  }

  activateAccount(userId: string, newCredentialsObj: any): Observable<any> {
    return this.userManagementService.resetPassword(userId, newCredentialsObj.password, newCredentialsObj.token).pipe(
      map(response => response),
      catchError((error) => {
        // If 404 (from lambda - user is not in UUM) then try ECA
        if (error.status === 404) {
          return this.activateAccountEca(newCredentialsObj);
        }
        return this.handleHttpError(error, UserSecurityService.badPasswordMessage);
      }));
  }

  updateWithNewPasswordEca(newCredentialsObj: any): Observable<any> {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/password/reset`;
    const reqOptions = { includeToken: false, cache: { cache: false } };
    return this.apiGatewayService.put<any>(url, newCredentialsObj, reqOptions).pipe(
      catchError(err => this.handleHttpError(err, UserSecurityService.badPasswordMessage)));
  }

  updateWithNewPassword(userId: string, newCredentialsObj: any): Observable<any> {
    return this.userManagementService.resetPassword(userId, newCredentialsObj.password, newCredentialsObj.token).pipe(
      catchError((error) => {
        // If 404 (from lambda - user is not in UUM) then try ECA
        if (error.status === 404) {
          return this.updateWithNewPasswordEca(newCredentialsObj);
        } else if (error.status === 403) {
          return this.handleHttpError(error, UserSecurityService.expiredPasswordMessage);
        }
        return this.handleHttpError(error, UserSecurityService.badPasswordMessage);
      }));
  }

  sendActivateAccountEmail(clientCode: string, token: string) {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/email/newUserRefresh/${token}`;
    return this.apiGatewayService.post(url, { clientCode: clientCode });
  }

  private handleHttpError(err: HttpErrorResponse, custMessage: string): Observable<PortalError> {
    const dataError = new PortalError();
    dataError.errorMessage = err.statusText;
    dataError.friendlyErrorMessage = custMessage;
    dataError.status = err.status;
    return throwError(dataError);
  }
}
